<template lang="pug">
#layout(:class='[name, isInIframe ? "embedded" : ""]')
  .wrapper.centered(:class="{ 'nav-open' : navOpened }")
    sidebar(:open='navOpened' @linkClicked='toggleSideBar')
    .main-panel(:class="{ 'loading' : loading }")
      ed-meta-nav(v-if="!isInIframe")
      navigation(v-if="!isInIframe" @toggleSideBar='toggleSideBar', :reference='reference')
      slot
      md-footer(v-if="!isInIframe")
      .close-layer(v-if='navOpened', @click='onCloseLayerClick')
</template>

<script>
import Navigation from '@/components/Navigation'
import MdFooter from '@/components/Footer'
import Sidebar from '@/components/Sidebar'

function perfNow () {
  return (window.performance && window.performance.now)
    ? window.performance.now()
    : Date.now()
}

export default {
  components: { Navigation, MdFooter, Sidebar },
  props: ['reference'],
  data () {
    return {
      name: 'layout-navigation',
      sidebarToggled: false,
      isInIframe: window.self !== window.top,
      // Observers
      resizeObserver: null,
      mutationObserver: null,
      // Target elements
      slotEl: null,
      modalEl: null,
      // Size state
      lastSent: { h: 0, w: 0 },
      lastChangeTs: 0,
      initialBurstCount: 0,
      // Timers / flags
      pollInterval: null,
      reconnectInterval: null,
      destroyed: false,
      rafHandle: 0,
      // History hook backups
      origPushState: null,
      origReplaceState: null
    }
  },
  computed: {
    navOpened () {
      return this.sidebarToggled
    },
    loading () {
      return this.$loading
    }
  },
  mounted () {
    this.bootstrap()
  },
  activated () {
    this.bootstrap()
  },
  beforeDestroy () {
    this.cleanup()
  },
  methods: {
    /* ---------- Initialization ---------- */
    bootstrap () {
      if (!this.isInIframe) return
      this.adjustShell()
      this.installMessageListener()
      this.setupContentObservers()
      this.installNavigationHooks()
      this.startReconnectWatcher()
      this.startPollingFallback()
      // Initial burst to ensure parent captures early size
      for (let i = 0; i < 3; i++) {
        setTimeout(() => this.postSize(), 60 * (i + 1))
      }
      // Post again after potential async layout settling
      setTimeout(() => this.postSize(), 500)
    },
    adjustShell () {
      this.$nextTick(() => {
        const mainPanel = this.$el.querySelector('.main-panel')
        if (mainPanel) mainPanel.style.overflow = 'hidden'
      })
    },

    /* ---------- Navigation Hooks ---------- */
    installNavigationHooks () {
      if (!this.origPushState && window.history && window.history.pushState) {
        this.origPushState = window.history.pushState
      }
      if (!this.origReplaceState && window.history && window.history.replaceState) {
        this.origReplaceState = window.history.replaceState
      }

      const self = this
      function afterNav () {
        // Reset burst so we aggressively re-measure after navigation
        self.initialBurstCount = 0
        // Immediate measurement to capture shrink ASAP
        self.postSize()
        // Debounced follow-up
        self.postSizeDebounced()
        // Additional delayed re-measurements for late-rendering content
        self.scheduleBurst([100, 250, 500, 800, 1200])
        // Notify parent
        try { window.parent.postMessage({ type: 'navigated' }, '*') } catch (e) {}
      }

      // Patch pushState
      if (this.origPushState) {
        try {
          window.history.pushState = function (...args) {
            const r = self.origPushState.apply(this, args)
            afterNav()
            return r
          }
        } catch (e) {}
      }

      // Patch replaceState
      if (this.origReplaceState) {
        try {
          window.history.replaceState = function (...args) {
            const r = self.origReplaceState.apply(this, args)
            afterNav()
            return r
          }
        } catch (e) {}
      }

      window.addEventListener('popstate', afterNav)
      window.addEventListener('hashchange', afterNav)

      // Vue router (best-effort)
      const vueGlobal = window.__VUE__ || window.__VUE_APP__ || window.app
      const possibleRouter = (vueGlobal && vueGlobal.$router) || window.__VUE_ROUTER__ || this.$router
      if (possibleRouter && !possibleRouter.__evedSizeHooked) {
        try {
          possibleRouter.afterEach(() => afterNav())
          possibleRouter.__evedSizeHooked = true
        } catch (e) {}
      }
    },

    /* ---------- PostMessage Listener ---------- */
    installMessageListener () {
      if (this.onMessage) window.removeEventListener('message', this.onMessage)
      this.onMessage = (e) => {
        if (e && e.data && e.data.type === 'request-size') {
          this.postSizeDebounced()
        }
      }
      window.addEventListener('message', this.onMessage)
    },

    /* ---------- Observers & Polling ---------- */
    setupContentObservers () {
      if (this.destroyed) return
      const el = this.$el.querySelector('.content.module')
      if (!el) {
        requestAnimationFrame(() => this.setupContentObservers())
        return
      }

      const modalEl = document.querySelector('.dawn-modal-container')

      if (this.slotEl === el && this.modalEl === modalEl) return

      this.disconnectObservers()
      this.slotEl = el
      this.modalEl = modalEl

      if (window.ResizeObserver) {
        this.resizeObserver = new window.ResizeObserver(() => {
          this.postSizeDebounced()
        })
        this.resizeObserver.observe(el)

        if (modalEl) {
          this.resizeObserver.observe(modalEl)
        }
      }

      if (window.MutationObserver) {
        this.mutationObserver = new window.MutationObserver(() => {
          this.postSizeDebounced()
        })
        this.mutationObserver.observe(el, {
          childList: true,
          subtree: true,
          attributes: true
        })

        if (modalEl) {
          this.mutationObserver.observe(modalEl, {
            childList: true,
            subtree: true,
            attributes: true
          })
        }
      }

      el.querySelectorAll('img,iframe,video,object,embed,picture source').forEach(node => {
        node.addEventListener('load', this.postSizeDebounced, { passive: true })
      })

      if (modalEl) {
        modalEl.querySelectorAll('img,iframe,video,object,embed,picture source').forEach(node => {
          node.addEventListener('load', this.postSizeDebounced, { passive: true })
        })
      }

      this.postSize()
    },
    startReconnectWatcher () {
      if (this.reconnectInterval) clearInterval(this.reconnectInterval)
      this.reconnectInterval = setInterval(() => {
        if (this.destroyed) return
        const current = this.$el.querySelector('.content.module')
        const currentModal = document.querySelector('.dawn-modal-container')
        if ((current && current !== this.slotEl) || (currentModal !== this.modalEl)) {
          this.setupContentObservers()
          this.postSizeDebounced()
        }
      }, 1000)
    },
    startPollingFallback () {
      if (this.pollInterval) clearInterval(this.pollInterval)
      this.pollInterval = setInterval(() => {
        if (this.destroyed) return
        if (perfNow() - this.lastChangeTs > 3000) {
          this.postSize()
        }
      }, 1500)
    },
    disconnectObservers () {
      if (this.resizeObserver) {
        try { this.resizeObserver.disconnect() } catch (e) {}
        this.resizeObserver = null
      }
      if (this.mutationObserver) {
        try { this.mutationObserver.disconnect() } catch (e) {}
        this.mutationObserver = null
      }
    },

    /* ---------- Cleanup ---------- */
    cleanup () {
      this.destroyed = true
      this.disconnectObservers()
      if (this.pollInterval) clearInterval(this.pollInterval)
      if (this.reconnectInterval) clearInterval(this.reconnectInterval)
      window.removeEventListener('message', this.onMessage)
      window.removeEventListener('popstate', this.postSizeDebounced)
      window.removeEventListener('hashchange', this.postSizeDebounced)
      cancelAnimationFrame(this.rafHandle)
      if (this.origPushState) {
        try { window.history.pushState = this.origPushState } catch (e) {}
      }
      if (this.origReplaceState) {
        try { window.history.replaceState = this.origReplaceState } catch (e) {}
      }
    },

    /* ---------- UI Interactions ---------- */
    toggleSideBar () {
      this.sidebarToggled = !this.sidebarToggled
      this.postSizeDebounced()
    },
    onCloseLayerClick () {
      this.toggleSideBar()
    },

    /* ---------- Measurement & Posting ---------- */
    openDropdownBottom () {
      let bottom = 0
      document.querySelectorAll('.dawnSelect__content-wrapper, .dawnSelect .toolbar').forEach(el => {
        if (!el.offsetParent) return // closed (v-show)
        bottom = Math.max(bottom, el.getBoundingClientRect().bottom + window.pageYOffset)
      })
      return bottom ? Math.ceil(bottom + 20) : 0
    },
    measureSize () {
      if (!this.slotEl) return { height: 0, width: 0 }
      const rect = this.slotEl.getBoundingClientRect()
      const styles = window.getComputedStyle(this.slotEl)
      const marginTop = parseFloat(styles.marginTop) || 0
      const marginBottom = parseFloat(styles.marginBottom) || 0
      let height = Math.ceil(rect.height + marginTop + marginBottom)

      if (this.modalEl) {
        const modalRect = this.modalEl.getBoundingClientRect()
        const modalStyles = window.getComputedStyle(this.modalEl)
        const modalMarginTop = parseFloat(modalStyles.marginTop) || 0
        const modalMarginBottom = parseFloat(modalStyles.marginBottom) || 0
        const modalPaddingTop = parseFloat(modalStyles.paddingTop) || 0
        const modalPaddingBottom = parseFloat(modalStyles.paddingBottom) || 0
        const modalHeight = Math.ceil(
          modalRect.height +
          modalMarginTop +
          modalMarginBottom +
          modalPaddingTop +
          modalPaddingBottom +
          20
        )
        height = Math.max(height, modalHeight)
      }

      // An open dropdown (e.g. the approval-rule user picker with its Apply button) overflows its
      // fixed-height field without growing any box, so without this the iframe is sized short and
      // the parent's footer covers the bottom of the dropdown (SPRT-19141).
      height = Math.max(height, this.openDropdownBottom())

      if (!height || height < 40) {
        height = Math.max(
          this.slotEl.scrollHeight,
          this.modalEl ? this.modalEl.scrollHeight : 0,
          document.documentElement ? document.documentElement.scrollHeight : 0,
          document.body ? document.body.scrollHeight : 0
        )
      }
      const width = Math.max(
        this.slotEl.scrollWidth,
        this.modalEl ? this.modalEl.scrollWidth : 0,
        document.documentElement ? document.documentElement.scrollWidth : 0
      )
      return { height, width }
    },
    postSize () {
      if (!this.slotEl || !this.isInIframe) return
      const { height, width } = this.measureSize()
      const shrinking = height < this.lastSent.h
      const changed = Math.abs(height - this.lastSent.h) > 2 || Math.abs(width - this.lastSent.w) > 2
      if (shrinking || changed || this.initialBurstCount < 5) {
        window.parent.postMessage(
          { type: 'resize', data: `if_height=${height}&if_width=${width}` },
          '*'
        )
        window.parent.postMessage(
          { type: 'resize', data: { height, width } },
          '*'
        )
        this.lastSent = { h: height, w: width }
        this.lastChangeTs = perfNow()
        this.initialBurstCount++
      }
    },
    postSizeDebounced () {
      cancelAnimationFrame(this.rafHandle)
      this.rafHandle = requestAnimationFrame(() => {
        this.$nextTick(() => this.postSize())
      })
    },
    scheduleBurst (delays = []) {
      delays.forEach(ms => {
        setTimeout(() => {
          if (!this.destroyed) this.postSize()
        }, ms)
      })
    }
  }
}
</script>

<style scoped lang="scss">
.navbar {
  &.no-tabs + .content.module:not(.legacy-container) {
    margin-top: 4.5rem;
  }
}

#layout.embedded >>> .content.module {
  margin-top: 0 !important;
}

.wrapper.centered {
  width: 100%;
}

.main-panel {
  position: relative;
  width: 100%;
  overflow: visible; // set to hidden dynamically for iframe mode
}
</style>



// WEBPACK FOOTER //
// src/components/Layouts/other.vue